import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import {Breadcrumb} from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { useForm } from 'react-hook-form';

const Help = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [expandedFaq, setExpandedFaq] = useState(null);

  // Mock user data for header
  const currentUser = {
    id: 1,
    name: "John Smith",
    email: "john.smith@example.com",
    avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face"
  };

  // Form for help ticket submission
  const { register, handleSubmit, formState: { errors }, reset } = useForm();

  const faqData = [
    {
      id: 1,
      category: 'account',
      question: 'How do I create an account?',
      answer: 'To create an account, click the "Sign Up" button on the login page. Fill in your email address, create a secure password, and verify your email. Your account will be activated once you confirm your email address.'
    },
    {
      id: 2,
      category: 'account',
      question: 'How can I reset my password?',
      answer: 'Click "Forgot Password" on the login page, enter your email address, and follow the instructions sent to your email. You\'ll receive a secure link to create a new password.'
    },
    {
      id: 3,
      category: 'events',
      question: 'How do I create a new event?',
      answer: 'Navigate to the dashboard and click "Create New Event". Enter the event name, description, and add participants by their email addresses. You can also set a budget and start date for better expense tracking.'
    },
    {
      id: 4,
      category: 'events',
      question: 'Can I add participants after creating an event?',
      answer: 'Yes! Go to your event details page and click "Add Participant". Enter their email address and they\'ll receive an invitation to join the event. They can view and add expenses once they accept.'
    },
    {
      id: 5,
      category: 'expenses',
      question: 'How do I split expenses unequally?',
      answer: 'When adding an expense, choose "Custom Split" instead of "Equal Split". You can then specify exact amounts or percentages for each participant. The total must equal 100% or the full expense amount.'
    },
    {
      id: 6,
      category: 'expenses',
      question: 'Can I add receipts to expenses?',
      answer: 'Yes, you can upload receipt images when creating or editing an expense. Supported formats include JPG, PNG, and PDF. This helps maintain accurate records and provides proof for expense claims.'
    },
    {
      id: 7,
      category: 'settlement',
      question: 'How do I settle up with someone?',
      answer: 'Go to your dashboard and find the person you want to settle with. Click "Settle Up" next to their name, choose the payment method, and mark it as paid. Both parties will receive a confirmation.'
    },
    {
      id: 8,
      category: 'settlement',
      question: 'What payment methods are supported?',
      answer: 'ExpenseLoop supports various payment methods including bank transfers, PayPal, Venmo, Cash App, and cash payments. You can also add custom payment methods in your settings.'
    }
  ];

  const categories = [
    { id: 'all', label: 'All Topics', icon: 'HelpCircle' },
    { id: 'account', label: 'Account Management', icon: 'User' },
    { id: 'events', label: 'Event Creation', icon: 'Calendar' },
    { id: 'expenses', label: 'Expense Tracking', icon: 'DollarSign' },
    { id: 'settlement', label: 'Settlement Process', icon: 'CreditCard' }
  ];

  const quickActions = [
    {
      title: 'How to Split Expenses',
      description: 'Learn the basics of expense splitting',
      icon: 'Pie',
      action: () => setExpandedFaq(5)
    },
    {
      title: 'Understanding Balances',
      description: 'See how balances are calculated',
      icon: 'Calculator',
      action: () => setExpandedFaq(7)
    },
    {
      title: 'Managing Participants',
      description: 'Add and remove event participants',
      icon: 'Users',
      action: () => setExpandedFaq(4)
    },
    {
      title: 'Payment Methods',
      description: 'Set up payment options',
      icon: 'CreditCard',
      action: () => setExpandedFaq(8)
    }
  ];

  const filteredFaqs = faqData?.filter(faq => {
    const matchesCategory = selectedCategory === 'all' || faq?.category === selectedCategory;
    const matchesSearch = searchQuery === '' || 
      faq?.question?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      faq?.answer?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    
    return matchesCategory && matchesSearch;
  });

  const handleFaqToggle = (faqId) => {
    setExpandedFaq(expandedFaq === faqId ? null : faqId);
  };

  const onSubmitTicket = async (data) => {
    console.log('Help ticket submitted:', data);
    // Here you would typically send the data to your support system
    alert('Your help ticket has been submitted. We\'ll get back to you within 24 hours!');
    reset();
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Help & Support - ExpenseLoop</title>
        <meta name="description" content="Get help with ExpenseLoop expense sharing features, account management, and technical support." />
      </Helmet>
      {/* <Header user={currentUser} isAuthenticated={true} /> */}
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Breadcrumb />
          
          {/* Header Section */}
          <div className="mb-8">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Icon name="HelpCircle" size={32} className="text-primary" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-foreground">Help & Support</h1>
                <p className="text-muted-foreground">
                  Find answers to common questions or get in touch with our support team
                </p>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mb-4">Quick Help</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {quickActions?.map((action, index) => (
                <button
                  key={index}
                  onClick={action?.action}
                  className="p-4 bg-card border border-border rounded-lg hover:bg-muted transition-micro text-left"
                >
                  <Icon name={action?.icon} size={24} className="text-primary mb-3" />
                  <h3 className="font-medium text-foreground mb-1">{action?.title}</h3>
                  <p className="text-sm text-muted-foreground">{action?.description}</p>
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2">
              {/* Search and Filter */}
              <div className="bg-card border border-border rounded-lg p-6 mb-6">
                <div className="space-y-4">
                  <div>
                    <label htmlFor="search" className="block text-sm font-medium text-foreground mb-2">
                      Search for help
                    </label>
                    <Input
                      id="search"
                      type="text"
                      placeholder="Search topics, questions, or keywords..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e?.target?.value)}
                      iconName="Search"
                      className="w-full"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Filter by category
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {categories?.map((category) => (
                        <button
                          key={category?.id}
                          onClick={() => setSelectedCategory(category?.id)}
                          className={`inline-flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-micro ${
                            selectedCategory === category?.id
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
                          }`}
                        >
                          <Icon name={category?.icon} size={16} />
                          <span>{category?.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* FAQ Section */}
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-foreground">
                    Frequently Asked Questions
                  </h2>
                  <span className="text-sm text-muted-foreground">
                    {filteredFaqs?.length} result{filteredFaqs?.length !== 1 ? 's' : ''}
                  </span>
                </div>

                {filteredFaqs?.length === 0 ? (
                  <div className="text-center py-8">
                    <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-foreground mb-2">No results found</h3>
                    <p className="text-muted-foreground mb-4">
                      Try adjusting your search terms or browse all topics
                    </p>
                    <Button 
                      variant="outline" 
                      onClick={() => {setSearchQuery(''); setSelectedCategory('all');}}
                    >
                      Clear filters
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {filteredFaqs?.map((faq) => (
                      <div key={faq?.id} className="border border-border rounded-lg">
                        <button
                          onClick={() => handleFaqToggle(faq?.id)}
                          className="w-full flex items-center justify-between p-4 text-left hover:bg-muted transition-micro"
                        >
                          <h3 className="font-medium text-foreground pr-4">{faq?.question}</h3>
                          <Icon 
                            name="ChevronDown" 
                            size={20} 
                            className={`text-muted-foreground transition-transform duration-200 ${
                              expandedFaq === faq?.id ? 'rotate-180' : ''
                            }`}
                          />
                        </button>
                        {expandedFaq === faq?.id && (
                          <div className="px-4 pb-4 pt-2">
                            <div className="border-t border-border pt-4">
                              <p className="text-muted-foreground leading-relaxed">{faq?.answer}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                {/* Contact Support */}
                <div className="bg-card border border-border rounded-lg p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <Icon name="MessageCircle" size={24} className="text-primary" />
                    <h3 className="text-lg font-semibold text-foreground">Still need help?</h3>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="p-3 bg-muted rounded-lg">
                      <div className="flex items-center space-x-2 mb-2">
                        <Icon name="Mail" size={16} className="text-muted-foreground" />
                        <span className="text-sm font-medium text-foreground">Email Support</span>
                      </div>
                      <p className="text-xs text-muted-foreground mb-2">
                        Get help via email within 24 hours
                      </p>
                      <Button size="sm" fullWidth iconName="Mail" iconPosition="left">
                        Send Email
                      </Button>
                    </div>

                    <div className="p-3 bg-muted rounded-lg">
                      <div className="flex items-center space-x-2 mb-2">
                        <Icon name="MessageSquare" size={16} className="text-muted-foreground" />
                        <span className="text-sm font-medium text-foreground">Live Chat</span>
                      </div>
                      <p className="text-xs text-muted-foreground mb-2">
                        Chat with our support team
                      </p>
                      <Button size="sm" variant="outline" fullWidth iconName="MessageSquare" iconPosition="left">
                        Start Chat
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Help Ticket Form */}
                <div className="bg-card border border-border rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-foreground mb-4">Submit a Ticket</h3>
                  
                  <form onSubmit={handleSubmit(onSubmitTicket)} className="space-y-4">
                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium text-foreground mb-1">
                        Subject *
                      </label>
                      <Input
                        id="subject"
                        type="text"
                        placeholder="Brief description of your issue"
                        {...register('subject', { required: 'Subject is required' })}
                        error={errors?.subject?.message}
                      />
                    </div>

                    <div>
                      <label htmlFor="priority" className="block text-sm font-medium text-foreground mb-1">
                        Priority
                      </label>
                      <select
                        id="priority"
                        {...register('priority')}
                        className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                        <option value="urgent">Urgent</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="description" className="block text-sm font-medium text-foreground mb-1">
                        Description *
                      </label>
                      <textarea
                        id="description"
                        rows={4}
                        placeholder="Please describe your issue in detail..."
                        {...register('description', { required: 'Description is required' })}
                        className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-none"
                      />
                      {errors?.description && (
                        <p className="mt-1 text-sm text-error">{errors?.description?.message}</p>
                      )}
                    </div>

                    <Button type="submit" fullWidth iconName="Send" iconPosition="left">
                      Submit Ticket
                    </Button>
                  </form>
                </div>

                {/* Additional Resources */}
                <div className="bg-card border border-border rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-foreground mb-4">Resources</h3>
                  
                  <div className="space-y-3">
                    <a href="#" className="flex items-center space-x-3 p-2 hover:bg-muted rounded-md transition-micro">
                      <Icon name="Book" size={16} className="text-muted-foreground" />
                      <span className="text-sm text-foreground">User Guide</span>
                    </a>
                    <a href="#" className="flex items-center space-x-3 p-2 hover:bg-muted rounded-md transition-micro">
                      <Icon name="Video" size={16} className="text-muted-foreground" />
                      <span className="text-sm text-foreground">Video Tutorials</span>
                    </a>
                    <a href="#" className="flex items-center space-x-3 p-2 hover:bg-muted rounded-md transition-micro">
                      <Icon name="FileText" size={16} className="text-muted-foreground" />
                      <span className="text-sm text-foreground">Terms of Service</span>
                    </a>
                    <a href="#" className="flex items-center space-x-3 p-2 hover:bg-muted rounded-md transition-micro">
                      <Icon name="Shield" size={16} className="text-muted-foreground" />
                      <span className="text-sm text-foreground">Privacy Policy</span>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Help;
